import axios from "axios";
import { ALL_FLAGS_OFF, type FeatureFlags } from "feature-flags";
import { useEffect, useState } from "react";
import type { ClientConfig } from "webapp";
import { onSiteProxyRequestInterceptor } from "../config/axios-interceptors";

export interface UseConfigResult {
  config: ClientConfig | null;
  isLoading: boolean;
  error: string | null;
}

// Global cache to avoid multiple requests
let globalConfig: ClientConfig | null = null;
let globalConfigPromise: Promise<ClientConfig> | null = null;

const axiosClient = axios.create();
axiosClient.interceptors.request.use(onSiteProxyRequestInterceptor);

export const fetchConfig = async (): Promise<ClientConfig> => {
  if (globalConfig) {
    return globalConfig;
  }

  if (globalConfigPromise) {
    return globalConfigPromise;
  }

  globalConfigPromise = axiosClient
    .get<ClientConfig>("/config/oAuth2.json", {
      params: { v: process.env.REACT_APP_BUILD_TIME },
    })
    .then((it) => it.data);
  globalConfig = await globalConfigPromise;

  return globalConfig;
};

/**
 * Returns the env-driven feature flags from `/config/oAuth2.json`. While the
 * config is loading (or if the fetch fails), every flag reads as `false` so
 * gated UI never flickers on. Backwards-compat: if `featureFlags` is missing
 * from the config (e.g. config served by an older deploy), defaults to all-off.
 */
export const useFeatureFlags = (): FeatureFlags => {
  const { config } = useConfig();
  return config?.featureFlags ?? ALL_FLAGS_OFF;
};

/**
 * Non-hook accessor for feature flags. Reads from the same global cache that
 * `useConfig` populates, so by the time any user-triggered action runs (every
 * tracking call is post-login) the cache is warm. Returns `ALL_FLAGS_OFF` when
 * the cache is cold so we fail closed: a feature stays disabled until the
 * client config has loaded.
 *
 * Use only from non-component code (event handlers in services, fire-and-forget
 * helpers). React components should call `useFeatureFlags()` instead so they
 * re-render when the config arrives.
 */
export const getFeatureFlagsFromCache = (): FeatureFlags =>
  globalConfig?.featureFlags ?? ALL_FLAGS_OFF;

export const useConfig = (): UseConfigResult => {
  const [config, setConfig] = useState<ClientConfig | null>(globalConfig);
  const [isLoading, setIsLoading] = useState(!globalConfig);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (globalConfig) {
      setConfig(globalConfig);
      setIsLoading(false);
      return;
    }

    const loadConfig = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const result = await fetchConfig();
        setConfig(result);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error occurred";
        setError(errorMessage);
        console.error("Loading config error:", errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    loadConfig();
  }, []);

  return {
    config,
    isLoading,
    error,
  };
};
